import { useMemo } from "react";
import type { ColorPatternGameProps, PatternColor } from "./types";
import { PATTERN_COLORS } from "./types";

/**
 * ColorPatternGame Component
 *
 * A vibrant color pattern matching game where children identify
 * which color comes next in a sequence. Features gorgeous
 * rainbow gradients, satisfying gem-like color blocks, and
 * delightful celebration animations.
 *
 * Supports patterns like:
 * - AB: red, blue, red, blue, ?
 * - ABB: red, blue, blue, red, blue, blue, ?
 * - ABC: red, blue, yellow, red, blue, yellow, ?
 *
 * @example
 * <ColorPatternGame
 *   pattern={['red', 'blue', 'red', 'blue', 'red']}
 *   missingIndex={4}
 *   options={['red', 'blue', 'yellow']}
 *   correctAnswer="red"
 *   onAnswer={handleAnswer}
 *   selectedAnswer={selected}
 * />
 */
export function ColorPatternGame({
  pattern,
  missingIndex,
  options,
  correctAnswer,
  onAnswer,
  selectedAnswer,
  isSubmitted = false,
  isCorrect = false,
  discipline = "patterns",
}: ColorPatternGameProps) {
  // Generate display pattern with visual feedback
  const displayPattern = useMemo(() => {
    return pattern.map((color, index) => ({
      color,
      isMissing: index === missingIndex,
      index,
    }));
  }, [pattern, missingIndex]);

  return (
    <div className="flex flex-col items-center gap-8 w-full">
      {/* Color Pattern Arena */}
      <div
        className="
          relative w-full
          bg-gradient-to-b from-white via-[#F8F8FF] to-[#F0F0FF]
          rounded-3xl p-6 md:p-8 lg:p-10
          border-4 border-transparent
          shadow-[0_8px_40px_rgba(162,155,254,0.2),inset_0_2px_0_rgba(255,255,255,0.9)]
        "
        style={{
          borderImage:
            "linear-gradient(135deg, #FF6B6B, #FFB347, #FFE66D, #98D89B, #74B9FF, #A29BFE) 1",
        }}
      >
        {/* Decorative elements */}
        <div className="absolute top-4 left-5 text-2xl opacity-70 animate-float">
          🌈
        </div>
        <div
          className="absolute top-6 right-6 text-xl opacity-50 animate-sparkle"
          style={{ animationDelay: "0.3s" }}
        >
          💎
        </div>
        <div
          className="absolute bottom-4 left-8 text-lg opacity-40 animate-sparkle"
          style={{ animationDelay: "0.6s" }}
        >
          ✨
        </div>
        <div
          className="absolute bottom-5 right-5 text-xl opacity-60 animate-sparkle"
          style={{ animationDelay: "0.9s" }}
        >
          🌟
        </div>

        {/* Pattern Display */}
        <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4 lg:gap-5">
          {displayPattern.map(({ color, isMissing, index }) => (
            <div
              key={`color-${index}`}
              className="relative"
              style={{
                animation: `star-pop 0.4s ease-out ${index * 80}ms both`,
              }}
            >
              {isMissing ? (
                // Missing slot with gem-like appearance
                <div
                  className={`
                    w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24
                    flex items-center justify-center
                    rounded-2xl
                    border-4 border-dashed
                    transition-all duration-300 ease-bounce
                    ${
                      selectedAnswer && isSubmitted && isCorrect
                        ? "border-meadow-500 bg-meadow-100/60 scale-105"
                        : selectedAnswer
                          ? "border-prism-blue bg-prism-blue/10 scale-102"
                          : "border-neutral-300/80 bg-white/60"
                    }
                  `}
                >
                  {selectedAnswer ? (
                    <div
                      className={`
                        w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16
                        rounded-xl
                        ${PATTERN_COLORS[selectedAnswer].bg}
                        ${PATTERN_COLORS[selectedAnswer].border}
                        border-3
                        shadow-[0_4px_12px_rgba(0,0,0,0.15)]
                        ${isSubmitted && isCorrect ? "animate-celebrate" : "animate-star-pop"}
                      `}
                    />
                  ) : (
                    <span className="text-3xl md:text-4xl text-neutral-400/80 animate-pulse font-display">
                      ?
                    </span>
                  )}

                  {/* Celebration sparkles */}
                  {isSubmitted && isCorrect && (
                    <>
                      <span
                        className="absolute -top-3 -left-3 text-xl animate-sparkle"
                        style={{ animationDelay: "0s" }}
                      >
                        ✨
                      </span>
                      <span
                        className="absolute -top-3 -right-3 text-xl animate-sparkle"
                        style={{ animationDelay: "0.15s" }}
                      >
                        🌟
                      </span>
                      <span
                        className="absolute -bottom-3 left-0 text-lg animate-sparkle"
                        style={{ animationDelay: "0.3s" }}
                      >
                        💎
                      </span>
                      <span
                        className="absolute -bottom-3 -right-3 text-xl animate-sparkle"
                        style={{ animationDelay: "0.45s" }}
                      >
                        ✨
                      </span>
                    </>
                  )}
                </div>
              ) : (
                // Filled color gem
                <div
                  className={`
                    w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24
                    rounded-2xl
                    ${PATTERN_COLORS[color].bg}
                    ${PATTERN_COLORS[color].border}
                    border-4
                    shadow-[0_6px_0_0_rgba(0,0,0,0.15),0_8px_20px_rgba(0,0,0,0.12)]
                    transition-transform duration-200
                    hover:scale-102
                  `}
                  aria-label={`${color} color`}
                >
                  {/* Inner gem highlight */}
                  <div
                    className="
                      w-full h-full
                      rounded-xl
                      bg-gradient-to-br from-white/30 via-transparent to-transparent
                    "
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Answer Options */}
      <div className="w-full">
        <h3 className="text-center font-display font-bold text-body-xl text-neutral-800 mb-5">
          Which color comes next?
        </h3>

        <div className="flex flex-wrap justify-center gap-4 md:gap-5">
          {options.map((color, index) => {
            const isSelected = selectedAnswer === color;
            const isCorrectOption = color === correctAnswer;
            const colorConfig = PATTERN_COLORS[color];

            return (
              <button
                key={`option-${color}-${index}`}
                type="button"
                onClick={() => !isSubmitted && onAnswer(color)}
                disabled={isSubmitted}
                className={`
                  relative
                  w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28
                  rounded-2xl
                  border-4
                  transition-all duration-200 ease-bounce
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-prism-blue/50
                  ${getOptionStyles(isSelected, isSubmitted, isCorrect, isCorrectOption)}
                `}
                style={{ animationDelay: `${index * 60}ms` }}
                aria-label={`Select ${color}`}
                aria-pressed={isSelected}
              >
                {/* Inner color gem */}
                <div
                  className={`
                    w-14 h-14 md:w-16 md:h-16 lg:w-18 lg:h-18
                    mx-auto
                    rounded-xl
                    ${colorConfig.bg}
                    shadow-[0_4px_12px_rgba(0,0,0,0.15)]
                  `}
                >
                  {/* Gem highlight */}
                  <div
                    className="
                      w-full h-full
                      rounded-xl
                      bg-gradient-to-br from-white/40 via-transparent to-transparent
                    "
                  />
                </div>

                {/* Selection indicator */}
                {isSelected && !isSubmitted && (
                  <div
                    className="
                      absolute inset-0
                      rounded-2xl
                      border-4 border-prism-blue
                      pointer-events-none
                    "
                  />
                )}

                {/* Correct answer sparkle */}
                {isSubmitted && isCorrectOption && (
                  <span className="absolute -top-2 -right-2 text-xl animate-sparkle">
                    ✨
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Get styles for option button based on state
 */
function getOptionStyles(
  isSelected: boolean,
  isSubmitted: boolean,
  isCorrect: boolean,
  isCorrectOption: boolean
): string {
  // After submission
  if (isSubmitted) {
    if (isCorrectOption) {
      return `
        bg-gradient-to-b from-meadow-200 via-meadow-300 to-meadow-400
        border-meadow-600
        shadow-[0_4px_0_0_theme(colors.meadow.700),0_8px_20px_rgba(40,167,69,0.25)]
        animate-celebrate
      `;
    }
    if (isSelected && !isCorrect) {
      return `
        bg-neutral-200 border-neutral-400
        opacity-50 translate-y-0.5
        shadow-[0_2px_0_0_theme(colors.neutral.400)]
      `;
    }
    return `
      bg-neutral-100 border-neutral-300
      opacity-35
      shadow-none
      cursor-default
    `;
  }

  // Selected but not submitted
  if (isSelected) {
    return `
      bg-gradient-to-b from-prism-blue/20 via-prism-blue/40 to-prism-blue/60
      border-prism-blue
      shadow-[0_4px_0_0_theme(colors.prism.blue.dark),0_8px_20px_rgba(116,185,255,0.3)]
      -translate-y-1.5 scale-[1.03]
    `;
  }

  // Default unselected state
  return `
    bg-white border-neutral-300
    shadow-[0_4px_0_0_theme(colors.neutral.300),0_6px_16px_rgba(0,0,0,0.06)]
    hover:border-prism-blue/70 hover:bg-prism-blue/5
    hover:shadow-[0_6px_0_0_rgba(116,185,255,0.3),0_10px_24px_rgba(116,185,255,0.15)]
    hover:-translate-y-1.5
    active:translate-y-0.5 active:shadow-[0_2px_0_0_theme(colors.neutral.300)]
    cursor-pointer
  `;
}

export default ColorPatternGame;
